// ============================================================================
// SupportTicketDetails.tsx — карточка ОБРАЩЕНИЯ: детали + действия специалиста
// (взять/снять назначение, статус, приоритет, ответ) и «Открыть чат» (личный
// чат пользователя с ботом «Поддержка» — якорь обращения). Управляющие действия
// показываются ТОЛЬКО специалистам поддержки/админу; SSoT прав — сервер.
// ============================================================================

import { useState } from 'react';
import { MessageSquare, UserCheck, UserMinus } from 'lucide-react';
import type { SupportTicket } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import { logErrorToJournal } from '@/lib/errors';
import {
  TICKET_PRIORITY_OPTIONS,
  TICKET_STATUS_OPTIONS,
  supportErrorText,
  supportPriorityClass,
  supportPriorityLabel,
  supportStatusClass,
  supportStatusLabel,
} from '@/lib/supportInbox';
import { SupportChannelBadge } from './SupportChannelBadge';
import { SupportReplyBlock } from './SupportReplyBlock';

interface Props {
  ticket: SupportTicket;
  /** Специалист поддержки/админ (гейт сервера — SSoT). */
  isStaff: boolean;
  /** Обновление карточки/очереди после действия. */
  onChanged: () => void;
  onOpenChat: () => void;
}

function moment(value: string): string {
  if (!value) return '';
  return formatValue(value, '', 'timestamp');
}

export function SupportTicketDetails({ ticket, isStaff, onChanged, onOpenChat }: Props) {
  const showError = useToastStore((s) => s.showError);
  const showSuccess = useToastStore((s) => s.showSuccess);
  const myId = useAuthStore((s) => (s.user?.id ? String(s.user.id) : ''));
  const [busy, setBusy] = useState(false);

  const t = ticket;

  const run = async (fn: () => Promise<unknown>, successText: string, logContext: string) => {
    setBusy(true);
    try {
      await fn();
      showSuccess(successText);
      onChanged();
    } catch (e) {
      logErrorToJournal(e, logContext);
      showError(supportErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  const assignToMe = () =>
    run(
      () => getSDK().support.assignTicket({ ticketId: t.id, assigneeId: myId }),
      'Обращение назначено на вас',
      'components/support/SupportTicketDetails.tsx:назначение',
    );

  const unassign = () =>
    run(
      () => getSDK().support.assignTicket({ ticketId: t.id, assigneeId: '' }),
      'Обращение возвращено в очередь',
      'components/support/SupportTicketDetails.tsx:снятие назначения',
    );

  const changeStatus = (status: string) =>
    run(
      () => getSDK().support.setTicketStatus({ ticketId: t.id, status }),
      `Статус: ${supportStatusLabel(status)}`,
      'components/support/SupportTicketDetails.tsx:статус',
    );

  const changePriority = (priority: string) =>
    run(
      () => getSDK().support.setTicketPriority({ ticketId: t.id, priority }),
      `Приоритет: ${supportPriorityLabel(priority)}`,
      'components/support/SupportTicketDetails.tsx:приоритет',
    );

  const selectClass = 'h-8 rounded-md border bg-transparent px-2 text-sm';

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-base font-semibold">{t.subject || '(без темы)'}</span>
          <span className={`rounded-full border px-2 py-0.5 text-[11px] ${supportStatusClass(t.status)}`}>
            {supportStatusLabel(t.status)}
          </span>
          <span className={`rounded-full border px-2 py-0.5 text-[11px] ${supportPriorityClass(t.priority)}`}>
            {supportPriorityLabel(t.priority)}
          </span>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
          <span>Автор: {t.requesterName || '—'}</span>
          <SupportChannelBadge channel={t.channel} withPrefix />
          <span>Создано: {moment(t.createdAt)}</span>
          <span>Сообщений: {t.messageCount}</span>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
          <span>Последнее сообщение: {moment(t.lastMessageAt) || '—'}</span>
          <span>Последний ответ: {moment(t.lastReplyAt) || '—'}</span>
          {t.resolvedAt && <span>Решено: {moment(t.resolvedAt)}</span>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={onOpenChat}>
          <MessageSquare className="mr-1 h-3.5 w-3.5" />Открыть чат
        </Button>
        {isStaff && t.assignedTo !== myId && (
          <Button size="sm" className="h-7 text-[11px]" onClick={() => void assignToMe()} disabled={busy}>
            <UserCheck className="mr-1 h-3.5 w-3.5" />Взять
          </Button>
        )}
        {isStaff && t.assignedTo && (
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => void unassign()} disabled={busy}>
            <UserMinus className="mr-1 h-3.5 w-3.5" />Снять
          </Button>
        )}
      </div>

      {isStaff && (
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={selectClass}
            value={t.status}
            onChange={(e) => void changeStatus(e.target.value)}
            disabled={busy}
            title="Статус обращения"
          >
            {TICKET_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            className={selectClass}
            value={t.priority}
            onChange={(e) => void changePriority(e.target.value)}
            disabled={busy}
            title="Приоритет обращения"
          >
            {TICKET_PRIORITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <span className="text-[11px] text-muted-foreground">
            {t.assignedName
              ? `Исполнитель: ${t.assignedName}${t.assignedTo === myId ? ' (вы)' : ''}`
              : 'Не назначено'}
          </span>
        </div>
      )}

      {isStaff ? (
        <SupportReplyBlock ticketId={t.id} onReplied={onChanged} />
      ) : (
        <div className="rounded-lg border bg-muted/20 px-3 py-2 text-[12px] text-muted-foreground">
          Ответы приходят в личный чат с «Поддержкой» — откройте чат, чтобы продолжить
          общение; новое сообщение обновит это обращение.
        </div>
      )}
    </div>
  );
}
